import { getUserRolesLabel } from "components/MemberList/userRoles";
import type { TButtonMenuOption } from "components/shared/ButtonOptionsMenu";
import type { ROLES } from "containers/ScopeContext/roles.enum";
import { formatCustomRoleId } from "helpers/utils";
import { t } from "i18next";
import type {
    DTOAssignNotificationPermissions,
    NotificationRoleOption,
    NotificationRolePermissions,
} from "store/notificationStore";
import type { RoleListItem } from "store/permissionsStore";
import type { Binary } from "types";

export type NotificationDetails = {
    title: string;
    description: string;
};

export type ScopedNotifications = Record<NotificationType, NotificationDetails>;

export const scopedNotifications: ScopedNotifications = {
    bportal_beneficiary_edit: {
        title: t("ScopedNotificationSettings.bportal_beneficiary_edit.title"),
        description: t("ScopedNotificationSettings.bportal_beneficiary_edit.description"),
    },
    bportal_beneficiary_add_document: {
        title: t("ScopedNotificationSettings.bportal_beneficiary_add_document.title"),
        description: t("ScopedNotificationSettings.bportal_beneficiary_add_document.description"),
    },
    urssaf_open: {
        title: t("ScopedNotificationSettings.urssaf_open.title"),
        description: t("ScopedNotificationSettings.urssaf_open.description"),
    },
    urssaf_end: {
        title: t("ScopedNotificationSettings.urssaf_end.title"),
        description: t("ScopedNotificationSettings.urssaf_end.description"),
    },
    term_end: {
        title: t("ScopedNotificationSettings.term_end.title"),
        description: t("ScopedNotificationSettings.term_end.description"),
    },
};

/**
 * Some notifications are scoped to specific roles chosen by the company.
 * It comes with a company hidden settings 'uses_scoped_notifications'
 */
export const notificationTypesArray = [
    "bportal_beneficiary_edit", // Beneficiary edited his own profile on the beneficiary portal
    "bportal_beneficiary_add_document", // Beneficiary added a document on the beneficiary portal
    "urssaf_open", // URSSAF declaration in coming + date
    "urssaf_end", // URSSAF declaration end in 7 days
    "term_end", // Term is reaching or has reached its end date.
] as const;

export type NotificationType = (typeof notificationTypesArray)[number];

/**
 * Format the notification roles options to be used in the AddRolesOptions component
 * @param notificationsRolesPermissions
 * @returns
 */
export const formatNotificationRolesOptions = (
    notificationsRolesPermissions: NotificationRolePermissions[],
    allRoles: RoleListItem[] = [],
): NotificationRoleOption => {
    return notificationsRolesPermissions.reduce((acc, { notificationType, shouldNotify }) => {
        acc[notificationType] = allRoles.map((role) => {
            const roleKey = role.isCustom && role.id !== undefined ? formatCustomRoleId(role.id) : role.name;
            const label = role.isCustom ? role.name : getUserRolesLabel(role.name as ROLES);

            return {
                id: roleKey,
                label,
                value: shouldNotify[roleKey] ? 1 : 0,
            };
        });
        return acc;
    }, {} as NotificationRoleOption);
};

/**
 * Format the new notification roles permissions to be used in the updateNotificationRoles fetch
 * @param notificationRolesPermissions - existing permissions from backend
 * @param chosenOptions - currently selected options
 * @param notificationType - notification type to update
 * @param allRoles - all roles from /company/roles
 * @returns {DTOAssignNotificationPermissions}
 */
export const formatNotificationRolesPayload = (
    notificationRolesPermissions: NotificationRolePermissions[],
    chosenOptions: TButtonMenuOption[],
    notificationType: NotificationType,
    allRoles: RoleListItem[],
): DTOAssignNotificationPermissions => {
    const chosenOptionIds = new Set(chosenOptions.map((option) => option.id));

    return notificationRolesPermissions.reduce((payload, nrp) => {
        if (nrp.notificationType === notificationType) {
            // Build payload from allRoles, using chosenOptions for selected state
            const rolesPayload = allRoles.reduce(
                (roles, role) => {
                    const roleKey = role.isCustom && role.id !== undefined ? formatCustomRoleId(role.id) : role.name;
                    roles[roleKey] = chosenOptionIds.has(roleKey) ? 1 : 0;
                    return roles;
                },
                {} as Record<string, Binary>,
            );
            payload[nrp.notificationType] = rolesPayload;
        } else {
            payload[nrp.notificationType] = nrp.shouldNotify;
        }
        return payload;
    }, {} as DTOAssignNotificationPermissions);
};
